import { observer } from 'mobx-react';
import React, { ReactNode, useContext } from 'react';
import classNames from 'classnames';
import { Box, Divider } from '@wix/design-system';
import { AppContextProvider } from '../../AppLoader';
import { DialogFormContainer } from '../../DialogContainer/DialogContainer';
import { BodyContainer } from '../../AuthPage/AuthPage';
import { DialogHeader } from '../../DialogHeader/DialogHeader';
import { CustomInput } from '../../CustomInput';
import inputFieldStyles from '../../InputField/InputField.scss';
import { shouldBlockNonDigitKey, hasNonDigits } from './onlyDigits';
import s from './EnforcementCard.scss';

export interface EnforcementInputField {
  value: string;
  isValid: boolean;
  isTouched: boolean;
  errorInfo: ReactNode;
  onChange(e: React.ChangeEvent<HTMLInputElement>): void;
  onBlur(): void;
}

interface EnforcementCardProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /**
   * Drives the card's bottom padding and the title font,
   */
  priority?: 'primary' | 'secondary';
  showBackButton?: boolean;
  backButtonCallback?: Function;
  primaryButton?: ReactNode;
  secondaryButton?: ReactNode;
  skip?: ReactNode;
  footer?: ReactNode;
  topRightAction?: ReactNode;
  /**
   * Optional stepper, rendered centered between the header and the body.
   */
  stepper?: ReactNode;
  /**
   * Vertical gap between the content column's items.
   * Defaults to `6` (the standard spacing);
   * override per-screen when a tighter layout is needed.
   */
  contentGap?: number | string;
  /** Screen content (e.g. the method radios) — rendered in the content column. */
  children?: ReactNode;
}

/**
 * shared card shell for the redesigned 2FA-enforcement screens.
 */
const EnforcementCardBase: React.FC<EnforcementCardProps> = observer(
  ({
    title,
    subtitle,
    priority = 'primary',
    showBackButton = false,
    backButtonCallback,
    primaryButton,
    secondaryButton,
    skip,
    footer,
    topRightAction,
    stepper,
    contentGap = 6,
    children,
  }) => {
    const {
      rootStore: {
        displayStore: { isDialogCentered },
      },
    } = useContext(AppContextProvider);
    const flow = priority === 'primary' ? 'authPage' : 'secondaryFlow';

    return (
      <Box
        direction="vertical"
        width="100%"
        align="center"
        className={classNames(s.root, {
          dialogCenteredWrapper: isDialogCentered,
        })}
      >
        <DialogFormContainer
          autoCenter={false}
          mainHeaderProps={{
            showBackButton,
            backButtonCallback,
            disableLogoClick: true,
          }}
        >
          <BodyContainer
            priority={priority}
            className={topRightAction ? s.card : undefined}
          >
            {topRightAction && (
              <Box className={s.topRightAction}>{topRightAction}</Box>
            )}
            <DialogHeader maxWidth="420px">
              <DialogHeader.Title as="h1" flow={flow}>
                {title}
              </DialogHeader.Title>
              {subtitle && (
                <DialogHeader.SubTitleSmall>
                  {subtitle}
                </DialogHeader.SubTitleSmall>
              )}
            </DialogHeader>
            {stepper && (
              <Box
                align="center"
                width="100%"
                maxWidth="420px"
                marginBottom={3}
              >
                {stepper}
              </Box>
            )}
            <Box
              direction="vertical"
              align="left"
              gap={contentGap}
              className={s.content}
            >
              {children}
              {(primaryButton || secondaryButton || skip) && (
                <Box direction="vertical" align="center" gap={3} width="100%">
                  {primaryButton}
                  {secondaryButton}
                  {skip}
                </Box>
              )}
            </Box>
            {footer && (
              <Box
                direction="vertical"
                align="center"
                width="100%"
                gap="24px"
                marginTop="24px"
              >
                <Divider className={s.footerDivider} />
                <Box align="center" width="100%">
                  {footer}
                </Box>
              </Box>
            )}
          </BodyContainer>
        </DialogFormContainer>
      </Box>
    );
  },
);

interface EnforcementCardInputProps {
  field: EnforcementInputField;
  label: string;
  dataHook?: string;
  autoFocus?: boolean;
  autocomplete?: string;
  onEnterPressed?: () => void;
  maxLength?: number;
  onlyDigits?: boolean;
}

const Input: React.FC<EnforcementCardInputProps> = observer(
  ({
    field,
    label,
    dataHook,
    autoFocus,
    autocomplete,
    onEnterPressed,
    maxLength,
    onlyDigits,
  }) => {
    const shouldError = !field.isValid && field.isTouched;
    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Block a paste that would introduce non-digits.
      if (onlyDigits && hasNonDigits(e.target.value)) {
        return;
      }
      field.onChange(e);
    };
    const onKeyDown = onlyDigits
      ? (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (shouldBlockNonDigitKey(e)) {
            e.preventDefault();
          }
        }
      : undefined;
    return (
      <Box
        direction="vertical"
        width="100%"
        className={classNames(
          inputFieldStyles.wrapperClassName,
          inputFieldStyles.rtlInputFix,
        )}
      >
        <CustomInput
          inputWithLabel
          label={label}
          dataHook={dataHook}
          autoFocus={autoFocus}
          autocomplete={autocomplete}
          maxLength={maxLength}
          onKeyDown={onKeyDown}
          value={field.value}
          onChange={onChange}
          onBlur={() => field.onBlur()}
          onEnterPressed={onEnterPressed}
          submitOnEnter={false}
          status={shouldError ? 'error' : undefined}
          statusMessage={shouldError ? field.errorInfo : undefined}
        />
      </Box>
    );
  },
);

export const EnforcementCard = Object.assign(EnforcementCardBase, { Input });
